(function (root, factory) {
  'use strict';

  var api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (root) {
    root.DisplayZoneDeliveryCore = api;
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  var DEFAULT_WEEKDAYS = [1, 2, 3, 4, 5];
  var DEFAULT_DELIVERY_WEEKDAYS = [1, 2, 3, 4, 5, 6];

  function normalizeKey(value) {
    return String(value || '')
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function toNonNegativeInteger(value) {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    var number = Number(value);

    if (!Number.isFinite(number) || number < 0) {
      return null;
    }

    return Math.ceil(number);
  }

  function pad(value) {
    return String(value).padStart(2, '0');
  }

  function dateToKey(date) {
    return [date.getUTCFullYear(), pad(date.getUTCMonth() + 1), pad(date.getUTCDate())].join('-');
  }

  function calendarDate(year, month, day) {
    return new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  }

  function addCalendarDays(date, count) {
    var next = new Date(date.getTime());
    next.setUTCDate(next.getUTCDate() + count);
    return next;
  }

  function validTimeZone(timeZone) {
    if (!timeZone) {
      return false;
    }

    try {
      new Intl.DateTimeFormat('en-US', { timeZone: timeZone }).format(new Date());
      return true;
    } catch (error) {
      return false;
    }
  }

  function zonedDateParts(now, timeZone) {
    var formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23'
    });
    var values = {};

    formatter.formatToParts(now).forEach(function (part) {
      if (part.type !== 'literal') {
        values[part.type] = Number(part.value);
      }
    });

    return {
      year: values.year,
      month: values.month,
      day: values.day,
      hour: values.hour,
      minute: values.minute
    };
  }

  function parseCutoff(value) {
    var text = String(value || '12:00').trim();
    var match = text.match(/^(\d{1,2}):(\d{2})$/);

    if (!match) {
      return { hour: 12, minute: 0 };
    }

    var hour = Number(match[1]);
    var minute = Number(match[2]);

    if (hour > 23 || minute > 59) {
      return { hour: 12, minute: 0 };
    }

    return { hour: hour, minute: minute };
  }

  function isPastCutoff(parts, cutoffText) {
    var cutoff = parseCutoff(cutoffText);
    return parts.hour * 60 + parts.minute >= cutoff.hour * 60 + cutoff.minute;
  }

  function createExcludedDateSet(globalDates, profileDates) {
    var set = new Set();

    [globalDates || [], profileDates || []].forEach(function (dates) {
      dates.forEach(function (date) {
        if (/^\d{4}-\d{2}-\d{2}$/.test(String(date))) {
          set.add(String(date));
        }
      });
    });

    return set;
  }

  function isBusinessDay(date, weekdays, excludedDates) {
    var eligibleWeekdays = Array.isArray(weekdays) && weekdays.length ? weekdays : DEFAULT_WEEKDAYS;
    return eligibleWeekdays.indexOf(date.getUTCDay()) !== -1 && !excludedDates.has(dateToKey(date));
  }

  function rollForwardToBusinessDay(date, weekdays, excludedDates) {
    var result = new Date(date.getTime());
    var guard = 0;

    while (!isBusinessDay(result, weekdays, excludedDates)) {
      result = addCalendarDays(result, 1);
      guard += 1;

      if (guard > 370) {
        throw new Error('Unable to find an eligible delivery day. Check the weekday and excluded-date settings.');
      }
    }

    return result;
  }

  function nextBusinessDay(date, weekdays, excludedDates) {
    return rollForwardToBusinessDay(addCalendarDays(date, 1), weekdays, excludedDates);
  }

  function addBusinessDays(date, count, weekdays, excludedDates) {
    var remaining = toNonNegativeInteger(count);
    var result = rollForwardToBusinessDay(date, weekdays, excludedDates);
    var guard = 0;

    if (remaining === null) {
      throw new Error('Business-day count must be zero or greater.');
    }

    while (remaining > 0) {
      result = addCalendarDays(result, 1);

      if (isBusinessDay(result, weekdays, excludedDates)) {
        remaining -= 1;
      }

      guard += 1;

      if (guard > 730) {
        throw new Error('Delivery range exceeds the supported calculation window.');
      }
    }

    return result;
  }

  function validateProfile(profile, options) {
    var errors = [];
    var values = {
      preparationMin: toNonNegativeInteger(profile.preparationMinBusinessDays),
      preparationMax: toNonNegativeInteger(profile.preparationMaxBusinessDays),
      transitMin: toNonNegativeInteger(profile.transitMinBusinessDays),
      transitMax: toNonNegativeInteger(profile.transitMaxBusinessDays),
      buffer: toNonNegativeInteger(profile.extraBufferBusinessDays || 0)
    };
    var timeZone = profile.timeZone || options.defaultTimeZone;

    if (profile.enabled !== true) {
      errors.push('The delivery profile is not enabled.');
    }

    ['preparationMin', 'preparationMax', 'transitMin', 'transitMax', 'buffer'].forEach(function (key) {
      if (values[key] === null) {
        errors.push(key + ' must be a number of business days.');
      }
    });

    if (values.preparationMin !== null && values.preparationMax !== null && values.preparationMax < values.preparationMin) {
      errors.push('Preparation maximum cannot be less than preparation minimum.');
    }

    if (values.transitMin !== null && values.transitMax !== null && values.transitMax < values.transitMin) {
      errors.push('Transit maximum cannot be less than transit minimum.');
    }

    if (!validTimeZone(timeZone)) {
      errors.push('A valid IANA time zone is required.');
    }

    return {
      valid: errors.length === 0,
      errors: errors,
      values: values,
      timeZone: timeZone
    };
  }

  function calculateEstimate(profile, now, options) {
    var calculationOptions = options || {};
    var currentTime = now instanceof Date ? now : new Date(now || Date.now());
    var validation = validateProfile(profile || {}, calculationOptions);

    if (!validation.valid || Number.isNaN(currentTime.getTime())) {
      return {
        valid: false,
        errors: validation.errors.concat(Number.isNaN(currentTime.getTime()) ? ['The current date is invalid.'] : [])
      };
    }

    var preparationWeekdays = Array.isArray(profile.shippingWeekdays) && profile.shippingWeekdays.length
      ? profile.shippingWeekdays
      : (calculationOptions.defaultShippingWeekdays || DEFAULT_WEEKDAYS);
    var deliveryWeekdays = Array.isArray(profile.deliveryWeekdays) && profile.deliveryWeekdays.length
      ? profile.deliveryWeekdays
      : (calculationOptions.defaultDeliveryWeekdays || DEFAULT_DELIVERY_WEEKDAYS);
    var excludedDates = createExcludedDateSet(
      calculationOptions.globalExcludedDates,
      profile.excludedDates
    );
    var parts = zonedDateParts(currentTime, validation.timeZone);
    var baseDate = calendarDate(parts.year, parts.month, parts.day);

    if (!isBusinessDay(baseDate, preparationWeekdays, excludedDates)) {
      baseDate = rollForwardToBusinessDay(baseDate, preparationWeekdays, excludedDates);
    } else if (isPastCutoff(parts, profile.cutoffTimeLocal || calculationOptions.defaultCutoffTimeLocal)) {
      baseDate = nextBusinessDay(baseDate, preparationWeekdays, excludedDates);
    }

    var earliestDays = validation.values.preparationMin + validation.values.transitMin + validation.values.buffer;
    var latestDays = validation.values.preparationMax + validation.values.transitMax + validation.values.buffer;
    var earliestShipDate = addBusinessDays(
      baseDate,
      validation.values.preparationMin,
      preparationWeekdays,
      excludedDates
    );
    var latestShipDate = addBusinessDays(
      baseDate,
      validation.values.preparationMax,
      preparationWeekdays,
      excludedDates
    );
    var earliest = addBusinessDays(
      earliestShipDate,
      validation.values.transitMin + validation.values.buffer,
      deliveryWeekdays,
      excludedDates
    );
    var latest = addBusinessDays(
      latestShipDate,
      validation.values.transitMax + validation.values.buffer,
      deliveryWeekdays,
      excludedDates
    );

    return {
      valid: true,
      earliest: earliest,
      latest: latest,
      earliestKey: dateToKey(earliest),
      latestKey: dateToKey(latest),
      earliestShipDateKey: dateToKey(earliestShipDate),
      latestShipDateKey: dateToKey(latestShipDate),
      acceptedDateKey: dateToKey(baseDate),
      totalMinBusinessDays: earliestDays,
      totalMaxBusinessDays: latestDays,
      timeZone: validation.timeZone,
      errors: []
    };
  }

  function formatDateRange(earliest, latest, locale, includeYear, monthStyle) {
    var language = locale || 'en-US';
    var requestedMonthStyle = monthStyle === 'short' ? 'short' : 'long';
    var earliestYear = earliest.getUTCFullYear();
    var latestYear = latest.getUTCFullYear();
    var sameYear = earliestYear === latestYear;
    var sameMonth = sameYear && earliest.getUTCMonth() === latest.getUTCMonth();
    var shouldIncludeYear = includeYear === true || !sameYear;
    var monthFormatter = new Intl.DateTimeFormat(language, {
      month: requestedMonthStyle,
      timeZone: 'UTC'
    });
    var fullFormatter = new Intl.DateTimeFormat(language, {
      month: requestedMonthStyle,
      day: 'numeric',
      year: shouldIncludeYear ? 'numeric' : undefined,
      timeZone: 'UTC'
    });

    if (dateToKey(earliest) === dateToKey(latest)) {
      return fullFormatter.format(earliest);
    }

    if (sameMonth) {
      var yearSuffix = shouldIncludeYear ? ', ' + earliestYear : '';
      return monthFormatter.format(earliest) + ' ' + earliest.getUTCDate() + '-' + latest.getUTCDate() + yearSuffix;
    }

    if (sameYear) {
      return fullFormatter.format(earliest) + '-' + fullFormatter.format(latest);
    }

    return new Intl.DateTimeFormat(language, {
      month: requestedMonthStyle,
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC'
    }).format(earliest) + '-' + new Intl.DateTimeFormat(language, {
      month: requestedMonthStyle,
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC'
    }).format(latest);
  }

  return {
    DEFAULT_WEEKDAYS: DEFAULT_WEEKDAYS,
    DEFAULT_DELIVERY_WEEKDAYS: DEFAULT_DELIVERY_WEEKDAYS,
    addBusinessDays: addBusinessDays,
    calculateEstimate: calculateEstimate,
    createExcludedDateSet: createExcludedDateSet,
    dateToKey: dateToKey,
    formatDateRange: formatDateRange,
    isBusinessDay: isBusinessDay,
    normalizeKey: normalizeKey,
    parseCutoff: parseCutoff,
    toNonNegativeInteger: toNonNegativeInteger,
    validateProfile: validateProfile,
    zonedDateParts: zonedDateParts
  };
});
